import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import axios from 'axios'; // Import Axios
import './addProperty.css';
import LoadingPopup from '../../../SharedView/LoadingPopup';
import PropertyDetailsSection from './PropertyDetailsSection';
import CustomPriceSection from './CustomPriceSection'; // adjust path if needed
import StandardQuantityPopup from './StandardQuantityPopup';

const cleanerOptions = [
    { value: 1, label: "1 Cleaner" },
    { value: 2, label: "2 Cleaners" },
    { value: 3, label: "3 Cleaners" },
    { value: 4, label: "4 Cleaners" }
];

const bedCountOptions = [
    { value: 0, label: "0 Bed" },
    { value: 1, label: "1 Bed" },
    { value: 2, label: "2 Beds" },
    { value: 3, label: "3 Beds" },
    { value: 4, label: "4 Beds" },
    { value: 5, label: "5 Beds" },
    { value: 6, label: "6 Beds" },
    { value: 7, label: "7 Beds" },
    { value: 8, label: "8 Beds" }
];

const bedroomOptions = [
    { value: 0, label: "Studio" },
    { value: 1, label: "1 Bedroom" },
    { value: 2, label: "2 Bedrooms" },
    { value: 3, label: "3 Bedrooms" },
    { value: 4, label: "4 Bedrooms" },
    { value: 5, label: "5 Bedrooms" },
    { value: 6, label: "6 Bedrooms" },
    { value: 7, label: "7 Bedrooms" },
    { value: 8, label: "8 Bedrooms" }
];

const bathroomOptions = [
    { value: 1, label: "1 Bathroom" },
    { value: 1.5, label: "1.5 Bathrooms" },
    { value: 2, label: "2 Bathrooms" },
    { value: 2.5, label: "2.5 Bathrooms" },
    { value: 3, label: "3 Bathrooms" },
    { value: 3.5, label: "3.5 Bathrooms" },
    { value: 4, label: "4 Bathrooms" },
    { value: 4.5, label: "4.5 Bathrooms" },
    { value: 5, label: "5 Bathrooms" },
    { value: 5.5, label: "5.5 Bathrooms" },
    { value: 6, label: "6 Bathrooms" },
    { value: 6.5, label: "6.5 Bathrooms" },
    { value: 7, label: "7 Bathrooms" },
    { value: 7.5, label: "7.5 Bathrooms" },
    { value: 8, label: "8 Bathrooms" }
];

function AddProperty() {
    const [clients, setClients] = useState([]);
    const [newProperty, setNewProperty] = useState({
        client_id: '',  // preselected from ?clientId= when arriving from a client page
        address_line_1: '',
        unit_number: '',
        bedrooms: '',
        bathrooms: '',
        area: '',
        google_maps_link: '',
        latitude: null,
        longitude: null,
        google_place_id: null,
        google_place_desc: '',
        is_linen_hire: false,
        is_beach_towel: false,
        is_custom_price: false,
        custom_price: '',
        is_custom_sq: false,
        standard_quantities: [],
        required_cleaners: 1,
        permanent_notes: '',
        warehouse_note: '',
        scheduling_note: '',
        is_id_required: false,
        is_company_id_required: false,
        is_accompany_id_required: false,
        is_permit_needed: false,
        property_access: '',
        wifi_name: '',
        wifi_password: '',
        single_beds: 0,
        double_beds: 1,
        super_king_beds: 0,
        is_dolce_gusto: false,
        is_nespresso: false,
    });

    const [isLoading, setIsLoading] = useState(false); // State for loading spinner
    const [feedbackMessage, setFeedbackMessage] = useState(''); // State for feedback message
    const [showCustomSQPopup, setShowCustomSQPopup] = useState(false);

    const handleEditCustomSQ = () => {
        if (newProperty.bedrooms === "" || newProperty.bedrooms === null || newProperty.bedrooms === undefined) {
            alert("Please select Bedrooms first.");
            return;
        }
        setShowCustomSQPopup(true);
    };

    const handleCloseSQPopup = () => {
        setShowCustomSQPopup(false);
    };

    const navigate = useNavigate();
    // Arriving from a client's page carries ?clientId= — preselect it
    // (owner, 2026-09-02). Applied once the client list resolves.
    const [searchParams] = useSearchParams();
    const preselectId = parseInt(searchParams.get('clientId'), 10);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const response = await axios.get('/api/clients');
                const clientOptions = response.data.map(client => ({
                    value: client.id,
                    label: client.display_name || `${client.first_name} ${client.last_name}`
                }));
                setClients(clientOptions);
                // The ?clientId= preselect lands only if it names a real client.
                if (Number.isFinite(preselectId) && clientOptions.some((c) => c.value === preselectId)) {
                    setNewProperty((prev) => (prev.client_id ? prev : { ...prev, client_id: preselectId }));
                }
            } catch (error) {
                console.error('Error fetching clients:', error);
            }
        };

        fetchClients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preselectId]);

    const handleAddProperty = async (e) => {
        e.preventDefault();

        /**
         * A PIN IS MANDATORY (2026-07-28).
         *
         * The place id is NOT — dropping the pin by hand on the map is a
         * legitimate way to locate a villa or a building Google does not list,
         * and that path deliberately clears google_place_id. What cannot be
         * optional is the location itself: eleven active properties reached
         * production with no coordinates at all, so the driver app had nothing
         * to navigate to and simply said "no location saved".
         *
         * Checked here rather than with a `required` attribute because the
         * coordinates come from the map, not from an input the browser can see.
         */
        // Same test the client-side property modals use (isValidCoord): a
        // coordinate must be finite AND meaningfully non-zero. `0` alone is
        // finite, and 0,0 is the Gulf of Guinea — which is exactly what the
        // eleven location-less properties are stored as.
        const isValidCoord = (n) => Number.isFinite(n) && Math.abs(n) > 0.000001;
        const lat = Number(newProperty.latitude);
        const lng = Number(newProperty.longitude);
        if (!isValidCoord(lat) || !isValidCoord(lng)) {
            setFeedbackMessage(
                'Pick the building on the map before saving — search for it, or '
                + 'click the map to drop a pin. Drivers navigate to that pin.'
            );
            return;
        }

        try {
            setIsLoading(true); // Show loading popup
            const payload = {
                ...newProperty,
                custom_price: newProperty.is_custom_price ? newProperty.custom_price : null,
                standard_quantities: newProperty.is_custom_sq ? newProperty.standard_quantities : [],
            };

            const response = await axios.post('/api/properties', payload);
            if (response.status === 201) {
                setFeedbackMessage('Property added successfully!');
                setTimeout(() => {
                    navigate('/properties'); // Redirect after success
                }, 1500);
            } else {
                console.error('Error adding property');
                setFeedbackMessage('Failed to add property. Please try again.');
            }
        } catch (error) {
            console.error('Error adding property:', error);

            const backendError = error.response?.data;

            if (backendError?.message) {
                // Use whatever the backend tells us
                setFeedbackMessage(`${backendError.message}. Please contact a manager.`);
            } else {
                setFeedbackMessage('Failed to add property. Please try again.');
            }
        } finally {
            setIsLoading(false); // Hide loading popup
        }
    };

    const handleClosePopup = () => {
        setFeedbackMessage(''); // Clear feedback message
    };

    const handleBedroomChange = (selectedOption) => {
        const selectedBedrooms = selectedOption.value;
        let doubleBeds = 1; // Default to 1 double bed, including for Studio
        let cleaners = 1; // Default 1 cleaner

        // Adjust double beds and cleaners based on bedroom count
        if (selectedBedrooms === 2) {
            doubleBeds = 2;
        } else if (selectedBedrooms >= 3) {
            doubleBeds = selectedBedrooms; // 3 or more bedrooms
            cleaners = 2; // For 3 or more bedrooms, 2 cleaners
        }

        setNewProperty({
            ...newProperty,
            bedrooms: selectedBedrooms,
            double_beds: doubleBeds,
            required_cleaners: cleaners
        });
    };

    return (
        <div className="add-property-container">
            <LoadingPopup
                isLoading={isLoading}
                feedbackMessage={feedbackMessage}
                onClose={handleClosePopup}
            />
            <h2>Add New Property</h2>
            <form className="new-property-form" onSubmit={handleAddProperty}>
                <div className="form-section">
                    <h3>Select Client</h3>
                    <Select
                        options={clients}
                        value={clients.find((c) => c.value === newProperty.client_id) || null}
                        onChange={(selectedOption) => setNewProperty({ ...newProperty, client_id: selectedOption.value })}
                        placeholder="Search and select a client by display name..."
                        isSearchable
                        required
                    />
                </div>

                <PropertyDetailsSection
                    /* New property: let the pin choose the area (2026-08-30).
                       On EDIT it only ever suggests — silently re-filing an
                       existing unit is not something a form should do. */
                    autoSelectArea
                    newProperty={newProperty}
                    setNewProperty={setNewProperty}
                />

                <div className="form-section">
                    <h3>Bedroom Configuration</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Bedrooms</label>
                            <Select
                                options={bedroomOptions}
                                value={bedroomOptions.find(o => o.value === newProperty.bedrooms) || null}
                                onChange={handleBedroomChange}
                                placeholder="Select"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Bathrooms</label>
                            <Select
                                options={bathroomOptions}
                                value={bathroomOptions.find(o => o.value === newProperty.bathrooms) || null}
                                onChange={(opt) => setNewProperty({ ...newProperty, bathrooms: opt.value })}
                                placeholder="Select"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Single Beds</label>
                            <Select
                                options={bedCountOptions}
                                value={bedCountOptions.find(option => option.value === newProperty.single_beds)}
                                onChange={(selectedOption) => setNewProperty({ ...newProperty, single_beds: selectedOption.value })}
                                placeholder="Select Single Beds"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Double Beds</label>
                            <Select
                                options={bedCountOptions}
                                value={bedCountOptions.find(option => option.value === newProperty.double_beds)}
                                onChange={(selectedOption) => setNewProperty({ ...newProperty, double_beds: selectedOption.value })}
                                placeholder="Select Double Beds"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Super King Beds</label>
                            <Select
                                options={bedCountOptions}
                                value={bedCountOptions.find(option => option.value === newProperty.super_king_beds) || bedCountOptions[0]}
                                onChange={(selectedOption) => setNewProperty({ ...newProperty, super_king_beds: selectedOption.value })}
                                placeholder="Select Super King Beds"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section property-services-section">
                    <h3>Service, Pricing & Amenities</h3>

                    {/* Row 1: Linen, Beach Towel, Custom Price, Staffing */}

                    <div className="form-row property-services-row">
                        {/* Standard staffing */}
                        <div className="form-group property-select-group">
                            <label className="property-toggle-label">Standard Staffing</label>
                            <Select
                                options={cleanerOptions}
                                value={cleanerOptions.find(
                                    (option) => option.value === newProperty.required_cleaners
                                )}
                                onChange={(selectedOption) =>
                                    setNewProperty({
                                        ...newProperty,
                                        required_cleaners: selectedOption.value,
                                    })
                                }
                                placeholder="Select"
                                required
                            />
                        </div>

                        {/* Linen Hire */}
                        <div className="form-group property-toggle-group">
                            <label className="property-toggle-label">Linen Hire</label>
                            <div className="property-toggle-inline">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={newProperty.is_linen_hire}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setNewProperty((p) => ({
                                                ...p,
                                                is_linen_hire: checked,
                                                // If Linen Hire is turned OFF, also turn OFF Beach Towel
                                                is_beach_towel: checked ? p.is_beach_towel : false,
                                            }));
                                        }}
                                    />
                                    <span className="slider"></span>
                                </label>
                            </div>
                        </div>

                        {/* Beach towel (only if LH enabled) */}
                        {newProperty.is_linen_hire && (
                            <div className="form-group property-toggle-group">
                                <label className="property-toggle-label">LH Beach Towel</label>
                                <div className="property-toggle-inline">
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={newProperty.is_beach_towel}
                                            onChange={(e) =>
                                                setNewProperty((p) => ({
                                                    ...p,
                                                    is_beach_towel: e.target.checked,
                                                }))
                                            }
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Custom Laundry Quantity */}
                        <div className="form-group property-toggle-group">
                            <label className="property-toggle-label">
                                Custom Laundry Quantity
                            </label>
                            <div className="property-toggle-inline">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={!!newProperty.is_custom_sq}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setNewProperty((p) => ({
                                                ...p,
                                                is_custom_sq: checked,
                                                standard_quantities: checked ? p.standard_quantities : [],
                                            }));
                                        }}
                                    />
                                    <span className="slider"></span>
                                </label>

                                {newProperty.is_custom_sq && (
                                    <span
                                        className="edit-custom-sq-link"
                                        onClick={handleEditCustomSQ}
                                    >
                                        Edit Custom Quantity
                                    </span>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* Row 2: Coffee amenities ONLY (no custom SQ here) */}
                    <div className="form-row property-services-row">
                        <div className="form-group property-amenities-group">
                            <label className="property-toggle-label">
                                Coffee Capsules Amenities
                            </label>
                            <div className="property-amenities-inline">
                                <div className="property-toggle-group small">
                                    <span className="property-toggle-label-sm">
                                        Nescafe Dolce Gusto
                                    </span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={!!newProperty.is_dolce_gusto}
                                            onChange={(e) =>
                                                setNewProperty({
                                                    ...newProperty,
                                                    is_dolce_gusto: e.target.checked,
                                                })
                                            }
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>

                                <div className="property-toggle-group small">
                                    <span className="property-toggle-label-sm">
                                        Nespresso
                                    </span>
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={!!newProperty.is_nespresso}
                                            onChange={(e) =>
                                                setNewProperty({
                                                    ...newProperty,
                                                    is_nespresso: e.target.checked,
                                                })
                                            }
                                        />
                                        <span className="slider"></span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Custom price section (with calculator) */}
                        <CustomPriceSection
                            propertyData={newProperty}
                            setPropertyData={setNewProperty}
                        />

                    </div>
                </div>

                <div className="form-group">
                    <label>Permanant Notes</label>
                    <textarea
                        value={newProperty.permanent_notes}
                        onChange={(e) =>
                            setNewProperty({ ...newProperty, permanent_notes: e.target.value })
                        }
                        placeholder="Notes for cleaners on each visit at this property."
                    />
                </div>

                <div className="form-group">
                    <label>Warehouse Note</label>
                    <textarea
                        value={newProperty.warehouse_note}
                        onChange={(e) =>
                            setNewProperty({ ...newProperty, warehouse_note: e.target.value })
                        }
                        placeholder="Permanent notes for warehouse team (e.g., linen specifics, packing reminders)."
                    />
                </div>

                <div className="form-group">
                    <label>Scheduling Note</label>
                    <textarea
                        value={newProperty.scheduling_note}
                        onChange={(e) =>
                            setNewProperty({ ...newProperty, scheduling_note: e.target.value })
                        }
                        placeholder="Internal scheduling remarks for this property."
                    />
                </div>

                {/* Scheduling access requirement toggles */}
                <div className="form-row scheduling-toggles-row">
                    <div className="form-group">
                        <label className="property-toggle-label">ID Required</label>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={!!newProperty.is_id_required}
                                onChange={(e) =>
                                    setNewProperty({
                                        ...newProperty,
                                        is_id_required: e.target.checked,
                                    })
                                }
                            />
                            <span className="slider"></span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="property-toggle-label">Company ID Required</label>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={!!newProperty.is_company_id_required}
                                onChange={(e) =>
                                    setNewProperty({
                                        ...newProperty,
                                        is_company_id_required: e.target.checked,
                                    })
                                }
                            />
                            <span className="slider"></span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="property-toggle-label">Accompany ID Required</label>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={!!newProperty.is_accompany_id_required}
                                onChange={(e) =>
                                    setNewProperty({
                                        ...newProperty,
                                        is_accompany_id_required: e.target.checked,
                                    })
                                }
                            />
                            <span className="slider"></span>
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="property-toggle-label">Permit Needed</label>
                        <label className="switch">
                            <input
                                type="checkbox"
                                checked={!!newProperty.is_permit_needed}
                                onChange={(e) =>
                                    setNewProperty({
                                        ...newProperty,
                                        is_permit_needed: e.target.checked,
                                    })
                                }
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Property Access Info</label>
                        <input
                            type="text"
                            value={newProperty.property_access}
                            onChange={(e) =>
                                setNewProperty({
                                    ...newProperty,
                                    property_access: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div className="form-group">
                        <label>Wi-Fi Name</label>
                        <input
                            type="text"
                            value={newProperty.wifi_name}
                            onChange={(e) =>
                                setNewProperty({ ...newProperty, wifi_name: e.target.value })
                            }
                        />
                    </div>
                    <div className="form-group">
                        <label>Wi-Fi Password</label>
                        <input
                            type="text"
                            value={newProperty.wifi_password}
                            onChange={(e) =>
                                setNewProperty({
                                    ...newProperty,
                                    wifi_password: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <button type="submit" className="submit-button">Submit</button>
            </form>

            {showCustomSQPopup && (
                <StandardQuantityPopup
                    propertyId={null} // new property mode
                    initialQuantities={newProperty.standard_quantities}

                    // ✅ pass config so defaults can be computed
                    bedrooms={newProperty.bedrooms}
                    singleBeds={newProperty.single_beds}
                    doubleBeds={newProperty.double_beds}
                    superKingBeds={newProperty.super_king_beds}
                    isBeachTowel={newProperty.is_beach_towel}

                    onSave={(quantities) => {
                        setNewProperty((p) => ({
                            ...p,
                            is_custom_sq: true,
                            standard_quantities: quantities,
                        }));
                    }}
                    onClose={handleCloseSQPopup}
                />
            )}

        </div>
    );
}

export default AddProperty;
